import { useMemberIds } from '../../hooks/useMemberIds'
import { Avatar } from '../../components/Avatar'
import { BotTag } from '../../components/BotTag'
import { Icon } from '../../components/Icon'
import { useIsOnline } from '../../hooks/useOnline'
import { botAcceptsMessages, peopleIds } from '../../lib/bots'
import { useUi } from '../../state/ui'
import { BotProfileButton } from '../bots/BotName'
import { channelDisplayName, dmPartnerId } from '../../lib/channels'
import { pluralize } from '../../lib/format'
import { isPlaceholderUser, managedInfo } from '../../lib/managed'
import { useEphemeral } from '../../state/ephemeral'
import { QuietChip } from './QuietChip'
import { ManagedBadge } from '../channels/ManagedBadge'
import { useConversation } from './ConversationContext'
import { useBack } from '../shell/useBack'

export function ConversationHeader({ onOpenDetails, onOpenPinned }: { onOpenDetails: (section?: 'notifications') => void; onOpenPinned: () => void }) {
  const back = useBack()
  const { channel, users, me } = useConversation()
  const name = channelDisplayName(channel, users, me.id)
  const bot = channel.type === 'bot_dm'
  const openBotProfile = useUi((s) => s.openBotProfile)
  const oneToOne = channel.type === 'dm' || bot
  const partnerId = dmPartnerId(channel, me.id, users)
  const partnerOnline = useIsOnline(bot ? null : partnerId)
  // Bots are members but not people: they are left out of counts and the avatar stack.
  const people = peopleIds(useMemberIds(channel), users)
  const onlineCount = useEphemeral((s) => people.filter((id) => s.online[id]).length)
  const partner = partnerId ? users.get(partnerId) : undefined
  const stack = people.filter((id) => id !== me.id).slice(0, 3)
  const pinned = channel.pinnedMessageIds.length
  // Channels and group chats can be managed; direct and bot conversations never are.
  const managed = managedInfo(channel)

  // As in every chat app, the name and picture at the top lead to who or what this is: a bot's profile, or the details.
  // A pointer shortcut; the details button (and a bot's name in the subtitle) is the keyboard way in, and the buttons
  // inside the heading (the quiet chip, the bot's name) keep doing their own thing.
  const openWho = () => (bot && partnerId ? openBotProfile(partnerId) : onOpenDetails())
  const onHeadingClick = (event: React.MouseEvent) => {
    if (!(event.target as Element).closest('button, a')) openWho()
  }

  let subtitle: React.ReactNode
  if (bot) {
    // A bot with a webhook is someone to talk to: describe it. Without one it only posts here.
    subtitle = (
      <>
        {partnerId ? (
          <BotProfileButton userId={partnerId} label={`About ${name}`} className="subtitle-bot-button">
            <BotTag />
          </BotProfileButton>
        ) : (
          <BotTag />
        )}
        <span>{botAcceptsMessages(channel) ? (partner?.description ?? 'Answers your messages') : 'Sends updates here'}</span>
      </>
    )
  } else if (channel.type === 'dm') {
    subtitle = (
      <>
        {partnerOnline ? <span className="online-dot" aria-hidden="true" /> : null}
        <span>
          {isPlaceholderUser(partner) ? 'Not signed in yet' : partnerOnline ? 'Online' : 'Offline'}
          {partner ? ` · @${partner.username}` : ''}
        </span>
      </>
    )
  } else {
    subtitle = (
      <span>
        {pluralize(people.length, 'member')}
        {onlineCount ? ` · ${onlineCount} online` : ''}
        {channel.quietCount ? ` · ${channel.quietCount} muted` : ''}
        {channel.topic ? ` · ${channel.topic}` : ''}
      </span>
    )
  }

  return (
    <header className="chat-header">
      <button type="button" className="icon-button back-button" aria-label={back.label} onClick={back.go}>
        <Icon name="back" />
      </button>
      {bot && partnerId ? (
        <Avatar user={partner} id={partnerId} name={name} onClick={openWho} />
      ) : oneToOne ? (
        <Avatar user={partner} id={partnerId ?? channel.id} name={name} online={partnerOnline} onClick={openWho} />
      ) : (
        <Avatar name={name} id={channel.id} variant={channel.type === 'channel' ? 'channel' : 'person'} onClick={openWho} />
      )}
      <div className="chat-heading tappable" onClick={onHeadingClick}>
        <h2 tabIndex={-1}>{name}</h2>
        <p className="chat-subtitle">
          {managed ? <ManagedBadge managed={managed} /> : null}
          {/* Before the rest of the line: what is quiet here matters more than presence or a topic, and never truncates. */}
          <QuietChip channel={channel} onOpenDetails={onOpenDetails} />
          {subtitle}
        </p>
      </div>
      {!oneToOne ? (
        <div className="member-stack" aria-hidden="true">
          {stack.map((id) => (
            <Avatar key={id} user={users.get(id)} id={id} />
          ))}
          {people.length - 1 > 3 ? <Avatar name={`+ ${people.length - 4}`} id="more" /> : null}
        </div>
      ) : null}
      <div className="chat-actions">
        {/* Only once there is something to show: with nothing pinned the drawer opens empty to say so. */}
        {pinned ? (
          <button type="button" className="icon-button" aria-label={`Pinned messages (${pinned})`} title="Pinned messages" onClick={onOpenPinned}>
            <Icon name="pin" />
            <span className="count-dot" aria-hidden="true">{pinned}</span>
          </button>
        ) : null}
        <button
          type="button"
          className="icon-button"
          aria-label={oneToOne ? 'Conversation details' : 'Channel details'}
          title="Details"
          onClick={() => onOpenDetails()}
        >
          <Icon name={oneToOne ? 'info' : 'users'} />
        </button>
      </div>
    </header>
  )
}
